import React, { useState, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './calendar-components/ui/dialog';
import { Button } from './calendar-components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './calendar-components/ui/popover';
import { Calendar } from './calendar-components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './calendar-components/ui/select';
import './calendar-styles.css';
import './dialog-styles.css';
import './schedule-layout.css';
import { getClassTypeName } from '../../constants/classTypes';
import { useAuth } from '../../react-query/useAuth';
import { useQuery, useQueryClient } from 'react-query';
import { getUsers } from '../../services/api_data';
import { logAuditEvent } from '../../services/api_audit';
import {
  fetchCoverage,
  claimRole,
  releaseRole,
  addMinutesToLocalIso,
  apiErrorMessage,
} from '../../services/api_coverage';
import { format, startOfWeek, endOfWeek, isValid } from 'date-fns';
import { toast } from 'react-toastify';

// Cell color is coverage, not class type: past classes are gray, then red
// (nobody), yellow (one of two coaches) and green (fully covered).
const STATUS = {
  open: { label: "Open", className: "cell-open" },
  half: { label: "Half claimed", className: "cell-half" },
  full: { label: "Claimed", className: "cell-full" },
  past: { label: "Past", className: "cell-past" },
};
const statusOf = (cls) => (cls.is_past ? "past" : cls.coverage);

// The API calls the second spot "lead"; the gym calls it the Co-coach
const ROLE_LABELS = { head: "Head", lead: "Co-coach" };

// Durations that don't need a badge on the cell
const STANDARD_DURATIONS = [60, 90];

// "17:15" -> "5:15P" for the narrow time column
const shortTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h >= 12 ? "P" : "A"}`;
};

// "17:15" -> "5:15 PM" for the class popup
const longTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

// Coverage keys are gym-local "YYYY-MM-DDTHH:MM:SS"
const classTime = (cls) => cls.key.slice(11, 16);

const isCrossFit = (cls) => cls.class_type === "crossfit";

// A past class only lists who actually coached it. Its open spots no longer
// matter, and a class that became two-coach later would otherwise show a
// co-coach spot that never existed back then.
const visibleRoles = (cls) => (cls.is_past ? cls.roles.filter((r) => r.coach) : cls.roles);

const ScheduleGrid = ({ onOpenMenu }) => {
  const { userData } = useAuth();
  const isAdmin = userData?.is_admin === true;
  const queryClient = useQueryClient();

  // Fetch users when the current user is an admin
  const { data: users = [] } = useQuery(
    ['Users'],
    getUsers,
    {
      enabled: isAdmin,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  // Extract coach names dynamically from users, sorted alphabetically
  const coachNames = useMemo(() => {
    if (!users || users.length === 0) return [];
    return users
      .map(user => user.first_name)
      .filter(name => name) // Filter out any undefined/null names
      .sort((a, b) => a.localeCompare(b)); // Sort alphabetically
  }, [users]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedCoaches, setSelectedCoaches] = useState({});
  const [busy, setBusy] = useState(false);
  // `busy` only disables the buttons after a re-render, so a fast double tap
  // can fire a second claim first. The ref blocks it synchronously.
  const claimInFlight = useRef(false);

  const weekStart = format(startOfWeek(currentDate), "yyyy-MM-dd");
  const weekEnd = format(endOfWeek(currentDate), "yyyy-MM-dd");

  const { data: coverage, isLoading, isError } = useQuery(
    ['coverage', weekStart, weekEnd],
    () => fetchCoverage({ startDate: weekStart, endDate: weekEnd }),
    { keepPreviousData: true }
  );

  const classes = useMemo(() => coverage?.classes ?? [], [coverage]);

  const classesByKey = useMemo(
    () => Object.fromEntries(classes.map((cls) => [cls.key, cls])),
    [classes]
  );

  // One row per class start time that occurs anywhere in the week
  const rowTimes = useMemo(
    () => [...new Set(classes.map(classTime))].sort(),
    [classes]
  );

  const weekDays = useMemo(() => {
    const [y, m, d] = weekStart.split("-").map(Number);
    return Array.from({ length: 7 }, (_, i) => new Date(y, m - 1, d + i));
  }, [weekStart]);

  // Re-read from the latest coverage so the popup updates after a claim
  const selectedClass = selectedKey ? classesByKey[selectedKey] : null;

  const formatDate = (date) => {
    const days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    return `${days[date.getDay()]} ${date.getDate()}`;
  };

  const handlePrevWeek = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const handleNextWeek = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleCalendarSelect = (date) => {
    if (isValid(date)) {
      setCalendarDate(date);
      setCurrentDate(date);
    }
  };

  const openClass = (cls) => {
    setSelectedCoaches({});
    setSelectedKey(cls.key);
  };

  // Refresh the grid plus anything else built from events (home, hours)
  const refreshSchedule = () =>
    queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'Users' });

  const handleClaim = async (cls, role, coachName) => {
    if (claimInFlight.current) return;
    claimInFlight.current = true;
    setBusy(true);
    try {
      const result = await claimRole({ cls, role, coachName });
      toast.success(
        isAdmin && coachName !== userData.first_name
          ? `${coachName} is on this class.`
          : "Class claimed."
      );
      logAuditEvent({
        action: 'claim',
        coach_name: coachName,
        coach_email: coachName === userData.first_name ? userData.email : undefined,
        class_type: cls.type || cls.class_type,
        start_time: cls.key,
        end_time: addMinutesToLocalIso(cls.key, cls.duration),
        event_id: result?.event?._id || null,
        meta: { source: 'ScheduleGrid', recurring: cls.recurring, role, actor: userData.first_name },
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Couldn't claim this class."));
    } finally {
      claimInFlight.current = false;
      setBusy(false);
      refreshSchedule();
    }
  };

  const handleUnclaim = async (cls, role) => {
    setBusy(true);
    try {
      await releaseRole(role.event_id);
      toast.success("Slot unclaimed.");
      logAuditEvent({
        action: 'drop',
        coach_name: role.coach?.first_name || 'unknown',
        coach_email: role.is_me ? userData.email : undefined,
        class_type: cls.type || cls.class_type,
        start_time: cls.key,
        end_time: addMinutesToLocalIso(cls.key, cls.duration),
        event_id: role.event_id,
        meta: { source: 'ScheduleGrid', view: 'schedule', role: role.role, actor: userData.first_name },
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Couldn't unclaim this class."));
    } finally {
      setBusy(false);
      refreshSchedule();
    }
  };

  const renderCell = (cls) => {
    const typeLabel = isCrossFit(cls) ? null : getClassTypeName(cls.class_type);
    const roles = visibleRoles(cls);
    return (
      <div className="cell-body">
        {cls.is_past && roles.length === 0 && (
          <span className="cell-name cell-name-open">No coach</span>
        )}
        {roles.map((role, i) => (
          <span
            key={role.role || i}
            className={`cell-name ${role.coach ? "" : "cell-name-open"} ${role.is_me ? "cell-name-me" : ""}`}
          >
            {role.coach ? role.coach.first_name : "Open"}
          </span>
        ))}
        {typeLabel && <span className="cell-type">{typeLabel}</span>}
        {!STANDARD_DURATIONS.includes(cls.duration) && (
          <span className="duration-badge">⏱ {cls.duration}m</span>
        )}
      </div>
    );
  };

  const renderRoleRow = (cls, role) => {
    const canEdit = isAdmin || !cls.is_past;
    const iHoldOtherRole = cls.roles.some((r) => r.is_me && r !== role);
    const roleKey = role.role || "single";
    const label = cls.capacity === 2 ? ROLE_LABELS[role.role] : "Coach";

    let action = null;
    if (role.coach) {
      if (canEdit && (isAdmin || role.is_me) && role.event_id) {
        action = (
          <Button variant="destructive" disabled={busy} onClick={() => handleUnclaim(cls, role)}>
            Unclaim
          </Button>
        );
      }
    } else if (canEdit && isAdmin) {
      const chosen = selectedCoaches[roleKey] || "";
      action = (
        <div className="role-assign">
          <Select
            onValueChange={(name) => setSelectedCoaches((prev) => ({ ...prev, [roleKey]: name }))}
            value={chosen}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={coachNames.length === 0 ? "Loading coaches..." : "Select a coach"} />
            </SelectTrigger>
            <SelectContent>
              {coachNames.length === 0 ? (
                <SelectItem value="loading" disabled>Loading coaches...</SelectItem>
              ) : (
                coachNames.map((coach) => (
                  <SelectItem key={coach} value={coach}>
                    {coach}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button
            disabled={busy || !chosen}
            onClick={() => handleClaim(cls, role.role, chosen)}
          >
            Assign
          </Button>
        </div>
      );
    } else if (canEdit) {
      action = iHoldOtherRole ? (
        <span className="role-note">You already coach this class</span>
      ) : (
        <Button disabled={busy} onClick={() => handleClaim(cls, role.role, userData.first_name)}>
          Claim
        </Button>
      );
    }

    return (
      <div key={roleKey} className="role-row">
        <div className="role-who">
          <span className="role-label">{label}</span>
          <span className={role.coach ? "role-name" : "role-name role-name-open"}>
            {role.coach ? `${role.coach.first_name} ${role.coach.last_name || ""}`.trim() : "Open"}
          </span>
        </div>
        {action}
      </div>
    );
  };

  return (
    <div className="schedule-fullscreen flex flex-col bg-gray-100 text-xs">
      <div className="flex items-center p-2 bg-white shadow">
        {onOpenMenu && (
          <button onClick={onOpenMenu} className="p-1" aria-label="Open menu">
            <Menu size={20} />
          </button>
        )}
        <div className="flex flex-grow justify-center items-center">
          <button onClick={handlePrevWeek} className="p-1" aria-label="Previous week">
            <ChevronLeft size={20} />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="text-sm font-bold">
                {`${format(weekDays[0], "M/d")} - ${format(weekDays[6], "M/d/yy")}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={calendarDate}
                onSelect={handleCalendarSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <button onClick={handleNextWeek} className="p-1" aria-label="Next week">
            <ChevronRight size={20} />
          </button>
        </div>
        {/* Mirrors the menu button so the week picker stays centered */}
        {onOpenMenu && <div style={{ width: 28 }} />}
      </div>
      <div className="schedule-legend p-1 bg-white shadow">
        {["open", "half", "full", "past"].map((key) => (
          <div key={key} className="flex items-center">
            <div className={`legend-swatch ${STATUS[key].className}`}></div>
            <span className="text-xxs">{STATUS[key].label}</span>
          </div>
        ))}
      </div>
      <div className="flex-grow overflow-hidden flex flex-col">
        <div className="flex-grow overflow-auto">
          {isError && !coverage ? (
            <p className="schedule-message">Couldn't load the schedule. Check your connection and reopen this page.</p>
          ) : isLoading && !coverage ? (
            <p className="schedule-message">Loading schedule...</p>
          ) : (
            <table className="w-full h-full border-collapse table-fixed">
              <thead>
                <tr>
                  <th className="schedule-time-col sticky top-0 left-0 z-20 bg-white"></th>
                  {weekDays.map((day) => (
                    <th
                      key={day.toISOString()}
                      className={`sticky top-0 z-10 p-1 text-center ${
                        isToday(day) ? "bg-blue-100" : "bg-white"
                      }`}
                    >
                      {formatDate(day)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowTimes.map((time) => (
                  <tr key={time}>
                    <td className="sticky left-0 z-10 bg-white border-r p-1 text-center">
                      {shortTime(time)}
                    </td>
                    {weekDays.map((day) => {
                      const cls = classesByKey[`${format(day, "yyyy-MM-dd")}T${time}:00`];
                      return (
                        <td
                          key={`${day.toISOString()}-${time}`}
                          className={`border p-1 ${cls ? `cursor-pointer ${STATUS[statusOf(cls)].className}` : "cell-none"}`}
                          onClick={cls ? () => openClass(cls) : undefined}
                        >
                          {cls ? renderCell(cls) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <Dialog
        open={!!selectedClass}
        onOpenChange={(open) => {
          if (!open) setSelectedKey(null);
        }}
      >
        <DialogContent className="dialog-content" style={{ overflowY: "auto" }}>
          {selectedClass && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {`${formatDate(new Date(`${selectedClass.key.slice(0, 10)}T12:00:00`))} - ${longTime(classTime(selectedClass))}`}
                </DialogTitle>
              </DialogHeader>
              <p className="class-sheet-meta">
                {getClassTypeName(selectedClass.class_type)}
                {` · ${selectedClass.duration} min`}
                {selectedClass.capacity === 2 ? " · 2 coaches" : ""}
                {selectedClass.is_past ? " · Past" : ""}
              </p>
              <div className="role-list">
                {/* Admins still see a past class's open spots so they can fix hours */}
                {(isAdmin ? selectedClass.roles : visibleRoles(selectedClass)).map((role) =>
                  renderRoleRow(selectedClass, role)
                )}
                {!isAdmin && visibleRoles(selectedClass).length === 0 && (
                  <p className="role-note">Nobody coached this class.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduleGrid;
